import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { apiErrorMessage } from '@/lib/api';
import { disableTotp, enableTotp, setupTotp, type TotpSetupInput } from './api';
import { sessionQueryOptions } from './queries';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type EnableStep = 'closed' | 'password' | 'confirm';

export function TotpCard({ enabled }: { enabled: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Double authentification
          <Badge variant={enabled ? 'default' : 'secondary'}>{enabled ? 'Activée' : 'Désactivée'}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>{enabled ? <DisableFlow /> : <EnableFlow />}</CardContent>
    </Card>
  );
}

function EnableFlow() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<EnableStep>('closed');
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);

  const passwordForm = useForm<TotpSetupInput>({ defaultValues: { password: '' } });
  const codeForm = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const submitPassword = passwordForm.handleSubmit(async (values) => {
    try {
      const result = await setupTotp(values);
      setSetup(result);
      setStep('confirm');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Mot de passe incorrect'));
    }
  });

  const submitCode = codeForm.handleSubmit(async (values) => {
    try {
      const user = await enableTotp(values);
      queryClient.setQueryData(sessionQueryOptions.queryKey, user);
      toast.success('Double authentification activée');
      close();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Code incorrect'));
    }
  });

  function close() {
    setStep('closed');
    setSetup(null);
    passwordForm.reset();
    codeForm.reset();
  }

  return (
    <>
      <Button
        onClick={() => {
          setStep('password');
        }}
      >
        Activer
      </Button>

      <Dialog open={step !== 'closed'} onOpenChange={(open) => !open && close()}>
        <DialogContent>
          {step === 'password' && (
            <>
              <DialogHeader>
                <DialogTitle>Confirmez votre mot de passe</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitPassword} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="totp-password">Mot de passe</Label>
                  <Input
                    id="totp-password"
                    type="password"
                    autoFocus
                    {...passwordForm.register('password', { required: true })}
                  />
                </div>
                <Button type="submit" disabled={passwordForm.formState.isSubmitting}>
                  Continuer
                </Button>
              </form>
            </>
          )}

          {step === 'confirm' && setup && (
            <>
              <DialogHeader>
                <DialogTitle>Scannez le code</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4">
                <div className="rounded-md bg-white p-3">
                  <QRCode value={setup.otpauthUri} size={180} />
                </div>
                <p className="text-center text-sm text-muted-foreground">
                  Ou saisissez ce secret à la main dans votre application d'authentification :
                  <br />
                  <code className="font-mono text-xs">{setup.secret}</code>
                </p>
                <form onSubmit={submitCode} className="flex w-full flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="totp-code">Code à six chiffres</Label>
                    <Input
                      id="totp-code"
                      inputMode="numeric"
                      autoFocus
                      {...codeForm.register('code', { required: true })}
                    />
                  </div>
                  <Button type="submit" disabled={codeForm.formState.isSubmitting}>
                    Activer
                  </Button>
                </form>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DisableFlow() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const form = useForm<{ password: string; code: string }>({ defaultValues: { password: '', code: '' } });

  const submit = form.handleSubmit(async (values) => {
    try {
      const user = await disableTotp(values);
      queryClient.setQueryData(sessionQueryOptions.queryKey, user);
      toast.success('Double authentification désactivée');
      setOpen(false);
      form.reset();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Mot de passe ou code incorrect'));
    }
  });

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        Désactiver
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Désactiver la double authentification</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="disable-password">Mot de passe</Label>
              <Input id="disable-password" type="password" autoFocus {...form.register('password', { required: true })} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="disable-code">Code à six chiffres</Label>
              <Input id="disable-code" inputMode="numeric" {...form.register('code', { required: true })} />
            </div>
            <Button type="submit" variant="destructive" disabled={form.formState.isSubmitting}>
              Désactiver
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
